import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { ShieldCheck, Loader2, Play, AlertTriangle, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});
const RISK = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-green-100 text-green-700' };
const SEV = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700' };

const PredictiveCompliancePage = () => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [atRisk, setAtRisk] = useState([]);
  const [userId, setUserId] = useState('');
  const [driverResult, setDriverResult] = useState(null);
  const [msg, setMsg] = useState('');

  const loadAtRisk = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/predictive-compliance/at-risk`, { headers: authHeaders() });
      if (res.status === 403) { setAvailable(false); return; }
      if (!res.ok) throw new Error('load failed');
      setAtRisk((await res.json()).at_risk_drivers || []);
      setAvailable(true);
    } catch { /* keep */ } finally { setLoading(false); }
  };
  useEffect(() => { loadAtRisk(); /* eslint-disable-next-line */ }, []);

  const predictDriver = async () => {
    setMsg(''); setDriverResult(null);
    if (!userId.trim()) { setMsg('Enter a driver user id.'); return; }
    const res = await fetch(`${API_URL}/api/predictive-compliance/driver/${encodeURIComponent(userId.trim())}`, { headers: authHeaders() });
    if (!res.ok) { setMsg('Prediction failed'); return; }
    setDriverResult(await res.json());
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="predictive-compliance-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-emerald-600" /> Predictive Compliance
          </h1>
          <p className="text-gray-600 mt-1">
            Explainable, advisory forecasts of compliance risk from existing records. Read-only — no compliance data is changed and every send remains human-approved.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="predictive-compliance-loading"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="predictive-compliance-unavailable">
            <p className="text-gray-700">Predictive Compliance is not currently available.</p>
          </div>
        ) : (
          <>
            {msg && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800" data-testid="predictive-compliance-msg">{msg}</div>}

            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-800 text-sm" data-testid="predictive-compliance-banner">
              <Info className="w-5 h-5" /> Advisory only — predictions never modify compliance records and never send notifications on their own.
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="predictive-compliance-driver-panel">
              <h2 className="font-semibold text-gray-900">Predict for a Driver</h2>
              <div className="flex gap-2">
                <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="driver user id"
                  data-testid="pc-userid-input" className="border rounded-lg px-3 py-2 text-sm flex-1" />
                <button onClick={predictDriver} data-testid="pc-predict-btn"
                  className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-emerald-700 transition-colors">
                  <Play className="w-4 h-4" /> Predict
                </button>
              </div>
              {driverResult && (
                <div data-testid="pc-driver-result" className="rounded-lg border border-gray-200 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Risk</span>
                    <span data-testid="pc-driver-risk" className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${RISK[driverResult.risk_level]}`}>{driverResult.risk_level}</span>
                    <span className="text-xs text-gray-500">score {driverResult.risk_score}</span>
                  </div>
                  {driverResult.predictions.length === 0 ? (
                    <p className="text-sm text-gray-500">No foreseeable compliance issues.</p>
                  ) : driverResult.predictions.map((p, i) => (
                    <div key={i} data-testid={`pc-driver-pred-${i}`} className="border-t border-gray-100 pt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{p.item_type}</span>
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${SEV[p.severity]}`}>{p.severity}</span>
                        <span className="text-xs text-gray-400">{p.predicted_issue} · {p.days_to_expiry} d</span>
                      </div>
                      <pre className="text-[11px] text-gray-600 whitespace-pre-wrap mt-1">{JSON.stringify(p.reasoning, null, 2)}</pre>
                    </div>
                  ))}
                  {driverResult.suggested_notifications?.length > 0 && (
                    <p className="text-xs text-emerald-700">Suggested notifications (advisory): {driverResult.suggested_notifications.join(', ')}</p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2" data-testid="predictive-compliance-at-risk">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> At-Risk Drivers ({atRisk.length})</h2>
              {atRisk.length === 0 ? (
                <div className="text-center py-8 text-gray-400" data-testid="pc-at-risk-empty">No drivers at risk.</div>
              ) : atRisk.map((d) => (
                <div key={d.user_id} data-testid={`pc-at-risk-${d.user_id}`} className="rounded-lg border border-gray-200 bg-white p-4 flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900">{d.user_id}</span>
                    <span className="text-xs text-gray-500 ml-2">{d.prediction_count} prediction(s)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${RISK[d.risk_level]}`}>{d.risk_level}</span>
                    <span className="text-xs text-gray-500">score {d.risk_score}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PredictiveCompliancePage;
